import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import ConfirmDialog from '@/components/results/confirmDialog/confirmDialog.vue';
import { Confirmed } from '@/components/results/confirmDialog/confirmDialog';
import RuleDialog from '@/components/results/ruleDialog/ruleDialog.vue';
import {
  closeRule, CommissionRule, Effective, effectiveRate, FoundUser, loadRules, lookupUser, PRODUCTS, productName, RuleDraft, RuleProduct,
  ruleWho, saveRule, Scope, sportName, SPORTS,
} from '@/results/commission.api';
import { ResultError } from '@/results/results.api';
import { whenText } from '@/results/format';

const LEVEL_ORDER: Record<Scope, number> = { GLOBAL: 0, WHITELABEL: 1, UPLINE: 2, USER: 3 };
const LEVEL_TEXT: Record<Scope, string> = { GLOBAL: 'Global', WHITELABEL: 'Site', UPLINE: "Agent's downline", USER: 'Player' };
const PRODUCT_ORDER = PRODUCTS.map((p) => p.key);

interface CheckRow {
  product: RuleProduct;
  label: string;
  effective: Effective | null;
  error: string;
}

/**
 * GS-904 — commission rules on the result site (the owner, 2026-10-14: "put commission rules on the result site";
 * GS-836's API, MATCH-ODDS-ENGINE.md §6.7). The rules the signed-in agent may see, by level, product and sport, current
 * or with their history; a rule is added or changed in the rule form (read back as a sentence) and closed with a
 * reason; "Check a player" asks the settlement's own resolver what a player pays now on each product — bookmaker, fancy
 * and premium are charged only by a rule that names them.
 */
@Component({ components: { ConfirmDialog, RuleDialog } })
export default class CommissionPanel extends Vue {
  @Prop({ default: '' }) readonly username!: string;

  private tab: 'rules' | 'check' = 'rules';
  private rules: CommissionRule[] = [];
  private admin = false;
  private loading = false;
  private error = '';
  private off = false;
  private history = false;
  /** 'any' = no filter. */
  private productFilter = 'any';
  private scopeFilter = 'any';
  private search = '';

  private form = { open: false, editing: null as CommissionRule | null, busy: false, error: '' };
  private closing = { open: false, rule: null as CommissionRule | null, title: '', lines: [] as string[], busy: false, error: '' };
  private check = { username: '', sport: '1', loading: false, error: '', user: null as FoundUser | null, rows: [] as CheckRow[] };

  get tabs(): Array<{ key: string; label: string }> {
    return [{ key: 'rules', label: 'Rules' + (this.rules.length ? ' (' + this.current.length + ')' : '') }, { key: 'check', label: 'Check a player' }];
  }

  get productTabs(): Array<{ key: string; label: string }> {
    return [{ key: 'any', label: 'Every product' }, ...PRODUCTS.filter((p) => p.key !== 'ALL').map((p) => ({ key: p.key, label: p.label }))];
  }

  get scopeTabs(): Array<{ key: string; label: string }> {
    return [{ key: 'any', label: 'All levels' }, { key: 'USER', label: 'Players' }, { key: 'UPLINE', label: 'Agents' },
      { key: 'WHITELABEL', label: 'Sites' }, { key: 'GLOBAL', label: 'Global' }];
  }

  get sportOptions(): Array<{ value: string; label: string }> {
    return SPORTS.map((s) => ({ value: String(s.id), label: s.label }));
  }

  get current(): CommissionRule[] {
    return this.rules.filter((r) => r.current);
  }

  /** The rules shown: the filters, then by level (global first), who, product and sport; closed ones after the current. */
  get shown(): CommissionRule[] {
    const q = this.search.trim().toLowerCase();
    return this.rules.filter((r) => (this.productFilter === 'any' || r.product === this.productFilter
      // an ALL rule reaches Match Odds (only): it shows there too
      || (this.productFilter === 'MATCH_ODDS' && r.product === 'ALL'))
      && (this.scopeFilter === 'any' || r.scope === this.scopeFilter)
      && (!q || ruleWho(r).toLowerCase().includes(q)))
      .sort((a, b) => Number(b.current) - Number(a.current) || LEVEL_ORDER[a.scope] - LEVEL_ORDER[b.scope]
        || ruleWho(a).localeCompare(ruleWho(b)) || PRODUCT_ORDER.indexOf(a.product) - PRODUCT_ORDER.indexOf(b.product)
        || (a.sportId || 0) - (b.sportId || 0) || b.id - a.id);
  }

  get emptyTitle(): string {
    return this.rules.length ? 'No rule matches' : 'No commission rules yet';
  }

  get emptyText(): string {
    return this.rules.length ? 'Clear a filter to see more.'
      : 'With no rule at any level, nobody is charged commission. Add one with New rule.';
  }

  created(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const answer = await loadRules(this.history);
      this.rules = answer.rules;
      this.admin = answer.admin;
      this.off = false;
    } catch (e) {
      const err = e as ResultError;
      this.off = err.code === 'NOT_ENABLED';
      this.error = this.off ? '' : err.message;
    } finally {
      this.loading = false;
    }
  }

  setHistory(on: boolean): void {
    this.history = !!on;
    this.load();
  }

  // ------------------------------------------------------------------------------------------------ words

  who(r: CommissionRule): string {
    return ruleWho(r);
  }

  level(r: CommissionRule): string {
    return LEVEL_TEXT[r.scope];
  }

  what(r: CommissionRule): string {
    const sport = sportName(r.sportId);
    return productName(r.product) + ' · ' + sport.charAt(0).toUpperCase() + sport.slice(1);
  }

  rate(value: string | null): string {
    return Number(value || 0).toFixed(2) + '%';
  }

  payee(r: CommissionRule): string {
    if (!r.enabled) {
      return 'No commission here: the search stops at this level';
    }
    return r.payee === 'RULE_OWNER' ? 'Paid to ' + ruleWho(r) : r.payee === 'HOUSE' ? 'Kept by the house'
      : "Paid to the player's direct manager";
  }

  by(r: CommissionRule): string {
    return r.createdByName || (r.createdBy ? 'user ' + r.createdBy : 'migration');
  }

  when(value: string | null): string {
    return whenText(value);
  }

  // ------------------------------------------------------------------------------------------------ add, change, close

  add(): void {
    this.form = { open: true, editing: null, busy: false, error: '' };
  }

  edit(r: CommissionRule): void {
    this.form = { open: true, editing: r, busy: false, error: '' };
  }

  async save(draft: RuleDraft): Promise<void> {
    this.form.busy = true;
    this.form.error = '';
    try {
      const saved = await saveRule(draft);
      this.form.open = false;
      this.$emit('toast', 'success', `${ruleWho(saved)}: ${productName(saved.product)} rule saved — `
        + (saved.enabled ? this.rate(saved.ratePct) : 'no commission') + '.');
      await this.load();
    } catch (e) {
      this.form.error = (e as Error).message;
    } finally {
      this.form.busy = false;
    }
  }

  askClose(r: CommissionRule): void {
    this.closing = {
      open: true, rule: r, busy: false, error: '',
      title: `Close ${ruleWho(r)}'s ${productName(r.product)} rule`,
      lines: [this.what(r) + ' · ' + (r.enabled ? this.rate(r.ratePct) : 'no commission'),
        r.scope === 'GLOBAL' ? 'From now nothing is configured at the global level: players with no other rule pay no commission.'
          : 'From now this level is not configured: the search falls through to the level above (an agent above, the site, global).',
        'To charge nothing here instead, change the rule to "No commission".'],
    };
  }

  async confirmClose(c: Confirmed): Promise<void> {
    const r = this.closing.rule;
    if (!r) {
      return;
    }
    this.closing.busy = true;
    this.closing.error = '';
    try {
      await closeRule(r.id, c.reason);
      this.closing.open = false;
      this.$emit('toast', 'success', `${ruleWho(r)}'s ${productName(r.product)} rule closed.`);
      await this.load();
    } catch (e) {
      this.closing.error = (e as Error).message;
    } finally {
      this.closing.busy = false;
    }
  }

  // ------------------------------------------------------------------------------------------------ check a player

  setCheckName(value: string): void {
    this.check.username = value;
  }

  /** What the player pays now on each product, from the settlement's own resolver (one call per product). */
  async runCheck(): Promise<void> {
    const name = this.check.username.trim();
    if (!name || this.check.loading) {
      return;
    }
    this.check.loading = true;
    this.check.error = '';
    this.check.user = null;
    this.check.rows = [];
    try {
      const user = await lookupUser(name);
      if (user.kind !== 'PLAYER') {
        this.check.error = `${user.username} is an agent: commission is charged to players — check one of its players.`;
        return;
      }
      this.check.user = user;
      const sport = Number(this.check.sport);
      const products = PRODUCTS.filter((p) => p.key !== 'ALL');
      const answers = await Promise.allSettled(products.map((p) => effectiveRate(user.id, p.key, sport)));
      this.check.rows = products.map((p, i) => {
        const a = answers[i];
        return { product: p.key, label: p.label, effective: a.status === 'fulfilled' ? a.value : null,
          error: a.status === 'rejected' ? (a.reason as Error).message : '' };
      });
    } catch (e) {
      this.check.error = (e as Error).message;
    } finally {
      this.check.loading = false;
    }
  }

  /** Where the answer came from, and who receives it. */
  source(row: CheckRow): string {
    const e = row.effective;
    if (!e) {
      return row.error;
    }
    if (!e.rule) {
      return 'No rule at any level names it: not charged.';
    }
    const from = `${ruleWho(e.rule)}'s ${productName(e.rule.product)} rule`;
    if (!e.charged) {
      return `${from} says no commission.`;
    }
    const to = e.rule.payee === 'HOUSE' ? 'kept by the house' : e.rule.payee === 'RULE_OWNER' ? 'paid to ' + ruleWho(e.rule)
      : 'paid to ' + (this.check.user && this.check.user.manager ? this.check.user.manager : 'the direct manager');
    const capped = Number(e.ratePct) < Number(e.rule.ratePct) ? ` (capped from ${this.rate(e.rule.ratePct)})` : '';
    return `From ${from}${capped}, ${to}.`;
  }

  checkSport(): string {
    return sportName(Number(this.check.sport));
  }
}
